import React, { useState, useRef, useEffect, useId } from 'react';
import { ChevronDown, X } from 'lucide-react';

// The ref reaches the combobox itself, so a popup can open with focus on it.
const SearchableSelect = React.forwardRef(({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  className = '',
  disabled = false,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}, ref) => {
  // A <div role="combobox"> is not labelable, so a <label htmlFor> cannot name
  // it: callers pass aria-labelledby (the label's id) or aria-label instead. The
  // search input that replaces the value while the list is open carries the
  // same name, since that is what has focus then.
  const nameProps = { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy };
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();

  // Find selected option label
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const displayValue = selectedOption ? selectedOption.label : '';

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const baseClasses = "w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500";
  const disabledClasses = disabled ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer";
  const combinedClasses = `${baseClasses} ${disabledClasses} ${className}`.trim();

  return (
    <div ref={containerRef} className="relative">
      {/* Display/Input Field */}
      <div
        className={combinedClasses}
        onClick={handleToggle}
        ref={ref}
        id={id}
        {...nameProps}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-disabled={disabled || undefined}
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        <div className="flex items-center justify-between gap-2">
          <span className={`flex-1 min-w-0 truncate block ${value ? 'text-gray-900' : 'text-gray-400'}`}>
            {isOpen ? (
              <input
                ref={inputRef}
                {...nameProps}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  // Stop propagation for navigation keys to prevent double handling
                  if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
                    e.stopPropagation();
                  }
                  handleKeyDown(e);
                }}
                className="w-full bg-transparent outline-none text-gray-900"
                placeholder={placeholder}
                autoFocus
                {...(dataTestId ? { 'data-testid': `${dataTestId}-input` } : {})}
              />
            ) : (
              displayValue || placeholder
            )}
          </span>
          <div className="flex items-center gap-1">
            {value && !disabled && (
              <X
                className="h-4 w-4 text-gray-400 hover:text-gray-600"
                onClick={handleClear}
              />
            )}
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          ref={listRef}
          id={listboxId}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`px-3 py-2 cursor-pointer text-sm ${
                  String(option.value) === String(value)
                    ? 'bg-primary-50 text-primary-900'
                    : index === highlightedIndex
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={String(option.value) === String(value)}
                {...(dataTestId
                  ? {
                      'data-testid': `${dataTestId}-option-${option.value}`,
                      'data-testid-group': `${dataTestId}-option`,
                    }
                  : {})}
              >
                {option.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
});

SearchableSelect.displayName = 'SearchableSelect';

export default SearchableSelect;

